/**
 * Service Dependency CRUD — composite (fromServiceId, toServiceId) key, so it
 * does not fit the single-id `createEntityCrudClient` shape.
 */

import { apiRequest } from './http';

export interface CreateDependencyInput {
  fromServiceId: string;
  toServiceId: string;
  dependencyType: 'hard' | 'soft';
  communicationPattern:
    'REST' | 'GraphQL' | 'gRPC' | 'Async' | 'EventDriven' | 'MessageQueue' | 'Unknown';
  criticality?: 'critical' | 'high' | 'medium' | 'low';
  isCircuitBroken?: boolean;
  averageCallsPerMinute?: number;
  averageLatencyMs?: number;
  notes?: string;
}

export interface UpdateDependencyInput {
  dependencyType?: 'hard' | 'soft';
  communicationPattern?:
    'REST' | 'GraphQL' | 'gRPC' | 'Async' | 'EventDriven' | 'MessageQueue' | 'Unknown';
  criticality?: 'critical' | 'high' | 'medium' | 'low';
  isCircuitBroken?: boolean;
  averageCallsPerMinute?: number;
  averageLatencyMs?: number;
  notes?: string;
}

export interface DependencyResponse {
  relType: string;
  fromEntityType: string;
  fromEntityId: string;
  toEntityType: string;
  toEntityId: string;
  metadata: {
    dependencyType: string;
    communicationPattern: string;
    criticality?: string;
    isCircuitBroken?: boolean;
    averageCallsPerMinute?: number;
    averageLatencyMs?: number;
    notes?: string;
  };
  _schemaVersion: string;
  _createdAt: string;
  _createdBy: string;
  _updatedAt?: string;
  _updatedBy?: string;
}

/**
 * Create a new service dependency
 */
export async function createDependency(input: CreateDependencyInput): Promise<DependencyResponse> {
  return apiRequest<DependencyResponse>('/dependencies', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/**
 * Update an existing service dependency
 */
export async function updateDependency(
  fromServiceId: string,
  toServiceId: string,
  input: UpdateDependencyInput
): Promise<DependencyResponse> {
  return apiRequest<DependencyResponse>(`/dependencies/${fromServiceId}/${toServiceId}`, {
    method: 'PUT',
    body: JSON.stringify(input),
  });
}

/**
 * Delete a service dependency
 */
export async function deleteDependency(fromServiceId: string, toServiceId: string): Promise<void> {
  return apiRequest<void>(`/dependencies/${fromServiceId}/${toServiceId}`, {
    method: 'DELETE',
  });
}
